import { Shapes } from '../../types'

type Constructor<T extends Shapes.Entity> = new (...args: any[]) => T

export class EntitiesRegister {
  // Stores entities indexed by their key, with each entity type (class) organized in its own map
  private entities: Map<Constructor<Shapes.Entity>, Map<string, Shapes.Entity>> = new Map()

  addEntity<T extends Shapes.Entity>(entity: T) {
    const ctor: Constructor<T> = entity.constructor as Constructor<T>
    if (!this.entities.has(ctor)) {
      this.entities.set(ctor, new Map<string, Shapes.Entity>())
    }
    this.entities.get(ctor)!.set(entity.key, entity)
  }

  getEntity<T extends Shapes.Entity>(ctor: Constructor<T>, key: string): T | undefined {
    const map = this.entities.get(ctor) as Map<string, T> | undefined
    return map?.get(key)
  }

  hasEntity<T extends Shapes.Entity>(ctor: Constructor<T>, key: string): boolean {
    const map = this.entities.get(ctor) as Map<string, T> | undefined
    return map?.has(key) ?? false
  }

  removeEntity<T extends Shapes.Entity>(entity: T): boolean {
    const ctor: Constructor<T> = entity.constructor as Constructor<T>
    const map = this.entities.get(ctor)
    if (map && entity.key) {
      return map.delete(entity.key)
    }
    return false
  }

  getEntities<T extends Shapes.Entity>(ctor: Constructor<T>): T[] {
    const map = this.entities.get(ctor) as Map<string, T> | undefined
    return map ? Array.from(map.values()) : []
  }

  allEntities(): Shapes.Entity[] {
    return Array.from(this.entities.values()).flatMap((map) => Array.from(map.values()))
  }

  clearEntities<T extends Shapes.Entity>(ctor: Constructor<T>) {
    this.entities.get(ctor)?.clear()
  }

  reset() {
    this.entities.forEach((map) => map.clear())
  }
}
